import { SignaturePiece } from '../signatures/SignaturesPage.js';
import { useState, type JSX } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Dialog, Input, PriceInput, Textarea } from '@artosera/ui';
import { SENS_MOUVEMENT, typeAvecBon } from '@artosera/shared';
import {
  useMouvement,
  useGesteMouvement,
  useEmettreBon,
  useVersCoffre,
  useModifierMouvement,
  mouvementsApi,
  type MouvementPresente,
} from '../../api/mouvements.js';
import { useContact } from '../../api/contacts.js';
import { useLieux } from '../../api/lieux.js';
import { useApercuMedia } from '../../api/medias.js';
import { estApiError } from '../../api/errors.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { galerieALaction, galerieAModule, galerieEcritModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { FeuillePiece } from '../../composants/FeuillePiece.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { PastilleStatut } from '../../composants/PastilleStatut.js';
import { Field } from '../Field.js';
import { TON_ETAT } from './mouvementUi.js';
import { RetourDialog } from './RetourDialog.js';
import { ConstatDialog } from './ConstatDialog.js';
import { CaissesMouvement } from '../inventaire/CaissesMouvement.js';
import styles from './MouvementDetailPage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

function Photo({ galerie, id, alt }: { galerie: string; id: string; alt: string }) {
  const apercu = useApercuMedia(galerie, id);
  return apercu.data ? <img src={apercu.data} alt={alt} className={styles.photo} /> : null;
}
function SuiviDialog({
  galerieSlug,
  m,
  onFermer,
}: {
  galerieSlug: string;
  m: MouvementPresente;
  onFermer: () => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const mutation = useModifierMouvement(galerieSlug);
  const [retour, setRetour] = useState(m.retourPrevuLe?.slice(0, 10) ?? '');
  const [valeur, setValeur] = useState(m.assurance?.valeurCentimes ?? m.valeurProposeeCentimes);
  const [police, setPolice] = useState(m.assurance?.police ?? '');
  const [assureur, setAssureur] = useState(m.assurance?.assureur ?? '');
  const [transporteur, setTransporteur] = useState(m.suivi?.transporteurNom ?? '');
  const [suivi, setSuivi] = useState(m.suivi?.numeroSuivi ?? '');
  const [url, setUrl] = useState(m.suivi?.urlSuivi ?? '');
  const [note, setNote] = useState(m.note);
  const [erreur, setErreur] = useState('');
  async function enregistrer() {
    try {
      await mutation.mutateAsync({
        id: m.id,
        corps: {
          retourPrevuLe: retour || null,
          assurance: {
            valeurCentimes: valeur,
            devise: m.assurance?.devise ?? 'EUR',
            ...(police ? { police } : {}),
            ...(assureur ? { assureur } : {}),
          },
          suivi: {
            ...(transporteur ? { transporteurNom: transporteur } : {}),
            ...(suivi ? { numeroSuivi: suivi } : {}),
            ...(url ? { urlSuivi: url } : {}),
          },
          note,
        },
      });
      onFermer();
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  return (
    <Dialog
      open
      title={t('regie:mouvements.fiche.modifier')}
      closeLabel={t('commun:actions.fermer')}
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
    >
      <div className={styles.form}>
        <Field htmlFor="suivi-retour" label={t('regie:mouvements.fiche.retourPrevu')}>
          <Input
            id="suivi-retour"
            type="date"
            value={retour}
            onChange={(e) => setRetour(e.target.value)}
          />
        </Field>
        <Field htmlFor="suivi-valeur" label={t('regie:mouvements.fiche.valeurAssuree')}>
          <PriceInput id="suivi-valeur" cents={valeur} onCentsChange={setValeur} />
        </Field>
        {(
          [
            ['police', police, setPolice],
            ['assureur', assureur, setAssureur],
            ['transporteur', transporteur, setTransporteur],
            ['numeroSuivi', suivi, setSuivi],
            ['urlSuivi', url, setUrl],
          ] as const
        ).map(([cle, v, set]) => (
          <Field key={cle} htmlFor={`suivi-${cle}`} label={t(`regie:mouvements.fiche.${cle}`)}>
            <Input id={`suivi-${cle}`} value={v} onChange={(e) => set(e.target.value)} />
          </Field>
        ))}
        <Field htmlFor="suivi-note" label={t('regie:mouvements.fiche.notes')}>
          <Textarea id="suivi-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        <Button disabled={mutation.isPending} onClick={() => void enregistrer()}>
          {t('regie:mouvements.fiche.enregistrer')}
        </Button>
      </div>
    </Dialog>
  );
}
export function MouvementDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun', 'oeuvres']);
  const { galerieSlug, galerie } = useShellContext();
  const { id } = useParams();
  const fiche = useMouvement(galerieSlug, id ?? null);
  const m = fiche.data;
  const contact = useContact(galerieSlug, m?.contactId ?? null);
  const lieux = useLieux(galerieSlug);
  const transport = useContact(galerieSlug, m?.transporteurId ?? null);
  const geste = useGesteMouvement(galerieSlug);
  const bon = useEmettreBon(galerieSlug);
  const coffre = useVersCoffre(galerieSlug);
  const [dialogue, setDialogue] = useState<'retour' | 'constat' | 'annuler' | 'suivi' | null>(null);
  const [raison, setRaison] = useState('');
  const [erreur, setErreur] = useState('');
  const ecrit = galerieALaction(galerie, 'movements:write');
  const range =
    ecrit && galerieEcritModule(galerie, 'coffre') && galerieALaction(galerie, 'documents:write');
  const date = (v: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone: 'UTC' }).format(
      new Date(v),
    );
  async function agir(fn: () => Promise<unknown>) {
    setErreur('');
    try {
      await fn();
    } catch (e) {
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
    }
  }
  if (fiche.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (!m)
    return (
      <div role="alert">
        <p>{t('commun:erreurReseau')}</p>
        <Button onClick={() => void fiche.refetch()}>{t('regie:mouvements.reessayer')}</Button>
      </div>
    );
  const lieu = lieux.data?.find((l) => l.id === m.versId)?.nom ?? '—';
  return (
    <section className={styles.page} data-ui="page">
      <Link className={styles.retour} to={`/${galerieSlug}/mouvements`}>
        {t('regie:mouvements.fiche.retourListe')}
      </Link>
      <header className={styles.entete} data-ui="page-heading">
        <div className={styles.titreLigne}>
          <h1 className={styles.titre}>{t(`regie:mouvements.types.${m.type}`)}</h1>
          <Badge tone={TON_ETAT[m.etat]}>{t(`regie:mouvements.etats.${m.etat}`)}</Badge>
        </div>
        <p className={styles.dates}>
          {date(m.departLe)}
          {m.retourPrevuLe && ` → ${date(m.retourPrevuLe)}`}
        </p>
        <p className={styles.meta}>
          {contact.data?.contact.nomAffiche ?? '—'} · {lieu}
        </p>
      </header>
      {erreur && (
        <p className={styles.error} role="alert">
          {erreur}
        </p>
      )}
      {ecrit && (
        <div className={styles.actions} data-ui="actions">
          {m.etat === 'prevu' && (
            <>
              <Button
                disabled={geste.isPending}
                onClick={() => void agir(() => geste.mutateAsync({ id: m.id, geste: 'depart' }))}
              >
                {t('regie:mouvements.fiche.depart')}
              </Button>
              <Button variant="danger" onClick={() => setDialogue('annuler')}>
                {t('regie:mouvements.fiche.annuler')}
              </Button>
            </>
          )}
          {m.etat === 'en_cours' &&
            (SENS_MOUVEMENT[m.type] === 'aller_retour' ? (
              <Button onClick={() => setDialogue('retour')}>
                {t('regie:mouvements.fiche.retour')}
              </Button>
            ) : (
              <Button
                disabled={geste.isPending}
                onClick={() => void agir(() => geste.mutateAsync({ id: m.id, geste: 'arrivee' }))}
              >
                {t('regie:mouvements.fiche.arrivee')}
              </Button>
            ))}
          {(m.etat === 'prevu' || m.etat === 'en_cours') && (
            <Button variant="secondary" onClick={() => setDialogue('suivi')}>
              {t('regie:mouvements.fiche.modifier')}
            </Button>
          )}
        </div>
      )}
      {typeAvecBon(m.type) && (
        <FeuillePiece
          emetteur={galerie.nom ?? galerieSlug}
          nature={t('regie:mouvements.fiche.bon')}
          numero={m.bon?.numero ?? t('regie:mouvements.fiche.bon')}
          destinataire={{
            legende: t(`regie:mouvements.parties.${m.type}`),
            nom: contact.data?.contact.nomAffiche ?? '—',
          }}
          brouillon={!m.bon}
        >
          <div className={styles.actions}>
            {m.bon ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() =>
                    void agir(async () =>
                      ouvrirUrlSignee((await mouvementsApi.bonPdf(galerieSlug, m.id)).url),
                    )
                  }
                >
                  {t('regie:mouvements.fiche.ouvrirBon')}
                </Button>
                {range && (
                  <Button
                    variant="secondary"
                    disabled={coffre.isPending || !!m.bon.documentId}
                    onClick={() => void agir(() => coffre.mutateAsync({ id: m.id, piece: 'bon' }))}
                  >
                    {t(
                      m.bon.documentId
                        ? 'regie:mouvements.fiche.rangeAuCoffre'
                        : 'regie:mouvements.fiche.rangerAuCoffre',
                    )}
                  </Button>
                )}
              </>
            ) : (
              ecrit &&
              m.etat !== 'annule' && (
                <Button
                  disabled={bon.isPending}
                  onClick={() => void agir(() => bon.mutateAsync(m.id))}
                >
                  {t('regie:mouvements.fiche.emettreBon')}
                </Button>
              )
            )}
          </div>
          {m.bon &&
            (m.type === 'depot' || m.type === 'pret') &&
            galerieAModule(galerie, 'signature-electronique') && (
              <SignaturePiece
                source={{ type: m.type === 'depot' ? 'bon_depot' : 'bon_pret', id: m.id }}
              />
            )}
        </FeuillePiece>
      )}
      {galerieAModule(galerie, 'reserve-inventaire') &&
        galerieALaction(galerie, 'inventaire:read') && (
          <CaissesMouvement key={m.id} mouvement={m} />
        )}
      <div className={styles.grid}>
        <section className={styles.panel} data-ui="panel">
          <h2>{t('regie:mouvements.fiche.assurance')}</h2>
          {(m.valeurProposeeSource === 'valeurs_assurance' ||
            m.valeurProposeeSource === 'mixte') && (
            <p>{t(`regie:mouvements.fiche.sources.${m.valeurProposeeSource}`)}</p>
          )}
          <p className={styles.valeur}>
            {m.assurance?.valeurCentimes === undefined
              ? '—'
              : new Intl.NumberFormat(i18n.language, {
                  style: 'currency',
                  currency: m.assurance.devise ?? 'EUR',
                }).format(m.assurance.valeurCentimes / 100)}
          </p>
          <p className={styles.detailPanneau}>
            {m.assurance?.assureur} {m.assurance?.police}
          </p>
        </section>
        <section className={styles.panel} data-ui="panel">
          <h2>{t('regie:mouvements.fiche.transport')}</h2>
          <p className={styles.valeur}>
            {m.suivi?.transporteurNom ?? transport.data?.contact.nomAffiche ?? '—'}
          </p>
          <p className={styles.detailPanneau}>{m.suivi?.numeroSuivi}</p>
          {m.suivi?.urlSuivi && /^https?:\/\//i.test(m.suivi.urlSuivi) && (
            <a href={m.suivi.urlSuivi} target="_blank" rel="noreferrer">
              {t('regie:mouvements.fiche.urlSuivi')}
            </a>
          )}
        </section>
      </div>
      <section className={styles.panel} data-ui="panel">
        <h2>{t('regie:mouvements.fiche.oeuvres')}</h2>
        {m.lignes.map((l) => (
          <div key={l.oeuvreId} className={styles.oeuvre}>
            <VignetteOeuvre url={l.apercuUrl ?? null} />
            <Link to={`/${galerieSlug}/oeuvres/${l.oeuvreId}`}>
              <ObjetCartel
                titre={l.titre}
                artiste={l.artisteNom}
                numeroInventaire={l.numeroInventaire}
              />
            </Link>
            {l.statut && <PastilleStatut statut={l.statut} />}{' '}
            {l.ignoreeAuRetour && (
              <p>
                {t('regie:mouvements.fiche.venduePendant', {
                  statut: t(`oeuvres:statuts.${l.ignoreeAuRetour}`),
                })}
              </p>
            )}
          </div>
        ))}
      </section>
      <section className={styles.panel} data-ui="panel">
        <div className={styles.panelEntete}>
          <h2>{t('regie:mouvements.fiche.constats')}</h2>
          <Verrouillable action="movements:write" ouvert={ecrit}>
            <Button variant="secondary" size="sm" onClick={() => setDialogue('constat')}>
              {t('regie:mouvements.fiche.nouveauConstat')}
            </Button>
          </Verrouillable>
        </div>
        {!m.constats.length && (
          <p className={styles.vide}>{t('regie:mouvements.fiche.aucunConstat')}</p>
        )}
        {m.constats.map((c) => (
          <article key={c.id} className={styles.constat}>
            <h3>
              {m.lignes.find((l) => l.oeuvreId === c.oeuvreId)?.titre} ·{' '}
              {t(`regie:mouvements.moments.${c.moment}`)}
            </h3>
            <Badge>{t(`regie:mouvements.etatsConstat.${c.etatGeneral}`)}</Badge>
            <p>{c.observations}</p>
            <p>
              {c.etabliPar} · {date(c.etabliLe)}
            </p>
            <div className={styles.photos}>
              {c.photos.map((photo) => (
                <Photo key={photo} galerie={galerieSlug} id={photo} alt={c.observations} />
              ))}
            </div>
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void agir(async () =>
                    ouvrirUrlSignee((await mouvementsApi.constatPdf(galerieSlug, m.id, c.id)).url),
                  )
                }
              >
                {t('regie:mouvements.fiche.pdfConstat')}
              </Button>
              {range && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={coffre.isPending || !!c.documentId}
                  onClick={() =>
                    void agir(() =>
                      coffre.mutateAsync({ id: m.id, piece: 'constat', constatId: c.id }),
                    )
                  }
                >
                  {t(
                    c.documentId
                      ? 'regie:mouvements.fiche.rangeAuCoffre'
                      : 'regie:mouvements.fiche.rangerAuCoffre',
                  )}
                </Button>
              )}
            </div>
          </article>
        ))}
      </section>
      {m.note && <p>{m.note}</p>}
      {dialogue === 'retour' && (
        <RetourDialog galerieSlug={galerieSlug} mouvement={m} onFermer={() => setDialogue(null)} />
      )}
      {dialogue === 'constat' && (
        <ConstatDialog galerieSlug={galerieSlug} mouvement={m} onFermer={() => setDialogue(null)} />
      )}
      {dialogue === 'suivi' && (
        <SuiviDialog galerieSlug={galerieSlug} m={m} onFermer={() => setDialogue(null)} />
      )}
      {dialogue === 'annuler' && (
        <Dialog
          open
          title={t('regie:mouvements.fiche.annuler')}
          closeLabel={t('commun:actions.fermer')}
          onOpenChange={(v) => {
            if (!v) setDialogue(null);
          }}
        >
          <div className={styles.form}>
            <Field htmlFor="annulation-raison" label={t('regie:mouvements.fiche.raisonAnnulation')}>
              <Input
                id="annulation-raison"
                value={raison}
                onChange={(e) => setRaison(e.target.value)}
              />
            </Field>
            <Button
              disabled={!raison.trim() || geste.isPending}
              onClick={() =>
                void agir(async () => {
                  await geste.mutateAsync({ id: m.id, geste: 'annuler', corps: { raison } });
                  setDialogue(null);
                })
              }
            >
              {t('regie:mouvements.fiche.annuler')}
            </Button>
            {erreur && <p role="alert">{erreur}</p>}
          </div>
        </Dialog>
      )}
    </section>
  );
}
